import { useState } from 'react';
import type { BankhubReconcileRow } from '@repo/shared-types';
import { Button } from '@repo/ui/src/components/ui/button';
import { Input } from '@repo/ui/src/components/ui/input';
import { Label } from '@repo/ui/src/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/src/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@repo/ui/src/components/ui/alert-dialog';
import { useCompanyStatus, useRemap, useUnlinkBank } from '../../hooks/useBankhub';

interface BankhubRowActionsProps {
  row: BankhubReconcileRow;
}

/** Per-row actions on the reconcile table. Only the actions that make sense
 *  for the row's drift flag are shown (see bankhubReconcile.ts for what each
 *  flag means): ORPHAN → Re-map or Terminate, DANGLING → Re-map only,
 *  GHOST_BANK → Unlink each bank. A row with no actionable flag renders a
 *  plain dash, same convention as UserRowActions for a no-op row. */
export function BankhubRowActions({ row }: BankhubRowActionsProps) {
  const [remapOpen, setRemapOpen] = useState(false);
  const [terminateOpen, setTerminateOpen] = useState(false);
  const companyStatus = useCompanyStatus();
  const unlinkBank = useUnlinkBank();

  // BankHub rejects every action on a Terminated company, so it must be
  // reactivated first. A leftover bank on ANY live company (orphan or ghost)
  // is a ghost link to clear — not only rows flagged GHOST_BANK (after a full
  // DB reset every company is ORPHAN, and the leftover bank sits inside it).
  const isTerminated = row.bankhubStatus.toLowerCase() === 'terminated';
  const hasBanks = row.banks.length > 0;

  const canActivate = isTerminated;
  const canRemap = !isTerminated && (row.flag === 'ORPHAN' || row.flag === 'DANGLING');
  const canUnlink = !isTerminated && hasBanks;
  const canTerminate = !isTerminated && row.flag === 'ORPHAN';

  if (!canActivate && !canRemap && !canUnlink && !canTerminate) {
    return <span className="text-surface-400">—</span>;
  }

  return (
    <div className="flex flex-wrap items-center justify-end gap-1.5">
      {canActivate && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={companyStatus.isPending}
          title={hasBanks ? 'Kích hoạt để có thể gỡ liên kết ngân hàng' : undefined}
          onClick={() => companyStatus.mutate({ xid: row.companyXid, status: 'Active' })}
        >
          Kích hoạt{hasBanks ? ' (để gỡ bank)' : ''}
        </Button>
      )}
      {canRemap && (
        <Button type="button" variant="outline" size="sm" onClick={() => setRemapOpen(true)}>
          Gán lại
        </Button>
      )}
      {canUnlink &&
        row.banks.map((bank) => (
          <Button
            key={bank.xid}
            type="button"
            variant="outline"
            size="sm"
            disabled={unlinkBank.isPending}
            onClick={() => unlinkBank.mutate({ companyXid: row.companyXid, bankXid: bank.xid })}
          >
            Gỡ {bank.bankName}
          </Button>
        ))}
      {canTerminate && (
        <Button type="button" variant="destructive" size="sm" onClick={() => setTerminateOpen(true)}>
          Chấm dứt
        </Button>
      )}

      <RemapDialog
        key={remapOpen ? row.companyXid : 'closed'}
        row={row}
        open={remapOpen}
        onOpenChange={setRemapOpen}
      />

      <AlertDialog open={terminateOpen} onOpenChange={setTerminateOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Chấm dứt company</AlertDialogTitle>
            <AlertDialogDescription>
              Company <strong>{row.companyName}</strong> ({row.companyXid}) sẽ được đổi status sang{' '}
              <strong>Terminated</strong> trên BankHub. Không thể hoàn tác.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={companyStatus.isPending}>Huỷ</AlertDialogCancel>
            <AlertDialogAction
              disabled={companyStatus.isPending}
              onClick={() =>
                companyStatus.mutate(
                  { xid: row.companyXid, status: 'Terminated' },
                  { onSuccess: () => setTerminateOpen(false) },
                )
              }
              className="bg-red-600 hover:bg-red-700"
            >
              Chấm dứt
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/** Re-map form: for an ORPHAN row (no org matched) the target org is picked
 *  fresh but the company is already this row's; for a DANGLING row (org
 *  points at a company that no longer exists at BankHub) the org is already
 *  this row's but the company xid needs to be re-entered. */
function RemapDialog({
  row,
  open,
  onOpenChange,
}: {
  row: BankhubReconcileRow;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const remap = useRemap();
  const [orgId, setOrgId] = useState(row.org?.id ?? '');
  const [companyXid, setCompanyXid] = useState(row.flag === 'ORPHAN' ? row.companyXid : '');

  const disabled = remap.isPending || !orgId.trim() || !companyXid.trim();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (disabled) return;
    remap.mutate(
      { orgId: orgId.trim(), companyXid: companyXid.trim() },
      { onSuccess: () => onOpenChange(false) },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Gán lại company</DialogTitle>
            <DialogDescription>
              Trỏ lại <code>sepayCompanyXid</code> của một tổ chức tới một company BankHub. Kiểm tra
              kỹ orgId và companyXid trước khi lưu, không có bước xác nhận thêm ở BankHub.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-1.5">
              <Label htmlFor="remap-org-id">Org ID</Label>
              <Input
                id="remap-org-id"
                value={orgId}
                onChange={(e) => setOrgId(e.target.value)}
                placeholder="ID tổ chức trên LopHoc"
                autoFocus
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="remap-company-xid">Company XID</Label>
              <Input
                id="remap-company-xid"
                value={companyXid}
                onChange={(e) => setCompanyXid(e.target.value)}
                placeholder="XID company trên BankHub"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={disabled}>
              {remap.isPending ? 'Đang lưu...' : 'Gán lại'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
